import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { api, extractError } from "@/lib/api";

const ACTION_LABEL = {
  account_created: "Account created",
  case_created: "New case opened",
  turn_submitted: "Submitted a turn",
  professional_case_blocked: "Tried to open a professional case (blocked)",
  consent_granted: "You granted consent",
  consent_revoked: "Consent was revoked",
};

function renderDetails(action, details = {}) {
  switch (action) {
    case "account_created":
      return `Age ${details.age} · guardian email: ${details.guardian_email || "—"}`;
    case "case_created":
      return `${details.title || "Untitled"} · ${details.tier || "free"} tier`;
    case "turn_submitted":
      return `Stage ${details.stage_index} · Participant ${details.participant} · ${details.word_count} words`;
    case "professional_case_blocked":
      return `Title: ${details.title || "—"} · reason: ${details.reason || ""}`;
    case "consent_granted":
      return `Granted by ${details.guardian_name || ""}`;
    case "consent_revoked":
      return `Guardian: ${details.guardian_email || ""}`;
    default:
      return JSON.stringify(details);
  }
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function statusContainerClass(status) {
  if (status === "granted") return "bg-[color:var(--surface-sage)] border-[color:var(--line)]";
  if (status === "revoked") return "bg-red-50 border-red-200";
  return "bg-white border-[color:var(--line)]";
}

function statusPillClass(status) {
  if (status === "granted") return "bg-green-100 text-green-800";
  if (status === "revoked") return "bg-red-100 text-red-700";
  return "bg-gray-100 text-gray-700";
}

function statusHelperText(consent) {
  if (consent.status === "granted") {
    return `Granted ${timeAgo(consent.granted_at)}. Paid upgrades and professional mediator are unlocked.`;
  }
  if (consent.status === "revoked") {
    return `Revoked ${timeAgo(consent.revoked_at)}. Paid upgrades and professional mediator are blocked.`;
  }
  return "No consent on file yet.";
}

function activityDotClass(action) {
  if (action === "consent_revoked" || action === "professional_case_blocked") {
    return "bg-[color:var(--counter-error)]";
  }
  if (action === "consent_granted") return "bg-green-600";
  return "bg-[color:var(--participant-a)]";
}

export default function GuardianDashboardPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revoking, setRevoking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/guardian/${token}`);
      setData(data);
    } catch (e) {
      setError(extractError(e, "Could not load guardian dashboard"));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const revoke = async () => {
    setRevoking(true);
    try {
      await api.post(`/guardian/${token}/revoke`);
      await load();
      setConfirmOpen(false);
    } catch (e) {
      setError(extractError(e, "Could not revoke consent"));
    } finally {
      setRevoking(false);
    }
  };

  const stats = useMemo(() => {
    if (!data) return null;
    const a = data.activities || [];
    return {
      cases: a.filter((x) => x.action === "case_created").length,
      turns: a.filter((x) => x.action === "turn_submitted").length,
      blocked: a.filter((x) => x.action === "professional_case_blocked").length,
    };
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-[color:var(--text-muted)]" data-testid="guardian-loading">
        Loading…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-md mx-auto px-6 py-16" data-testid="guardian-error">
        <h1 className="font-serif text-2xl mb-1">Dashboard unavailable</h1>
        <p className="text-sm text-[color:var(--counter-error)]">{error || "Link invalid."}</p>
      </div>
    );
  }

  const status = data.consent.status;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 fade-in" data-testid="guardian-dashboard">
      <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--participant-a)] font-medium">
        Guardian dashboard
      </p>
      <h1 className="font-serif text-3xl md:text-4xl mt-1 text-[color:var(--text-primary)]">
        Activity for {data.minor.name}
      </h1>
      <p className="text-sm text-[color:var(--text-muted)] mt-1">
        Hi {data.guardian.name || "Guardian"}. This page shows everything{" "}
        {data.minor.name} ({data.minor.email}, age {data.minor.age}) has done
        on FairResolver. You can revoke your consent at any time.
      </p>

      <div className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="guardian-stats">
        <Stat label="Cases opened" value={stats?.cases ?? 0} />
        <Stat label="Turns submitted" value={stats?.turns ?? 0} />
        <Stat
          label="Blocked attempts"
          value={stats?.blocked ?? 0}
          accent="warn"
        />
      </div>

      {/* Consent status + revoke */}
      <div
        className={`mt-6 rounded-2xl border p-5 flex flex-wrap items-center justify-between gap-3 ${statusContainerClass(status)}`}
        data-testid="guardian-consent-status-block"
      >
        <div>
          <div className="font-medium">
            Consent status:{" "}
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-xs ${statusPillClass(status)}`}
              data-testid="guardian-status-pill"
            >
              {status}
            </span>
          </div>
          <p className="text-xs text-[color:var(--text-muted)] mt-1">
            {statusHelperText(data.consent)}
          </p>
        </div>
        {status === "granted" && (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="px-4 py-2 rounded-full bg-[color:var(--counter-error)] text-white text-sm font-semibold hover:opacity-90"
            data-testid="guardian-revoke-btn"
          >
            Revoke consent
          </button>
        )}
      </div>

      {/* Activity log */}
      <h2 className="font-serif text-2xl mt-10 mb-3">Activity log</h2>
      {data.activities.length === 0 ? (
        <p className="text-sm text-[color:var(--text-muted)]" data-testid="guardian-activities-empty">
          No activity yet.
        </p>
      ) : (
        <ol className="space-y-2" data-testid="guardian-activity-list">
          {data.activities.map((a, i) => (
            <li
              key={`${a.at}-${i}`}
              className="rounded-xl bg-white border border-[color:var(--line)] p-4 flex items-start gap-3"
              data-testid={`activity-${a.action}-${i}`}
            >
              <span
                className={`mt-1 inline-block w-2 h-2 rounded-full ${activityDotClass(a.action)}`}
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-[color:var(--text-primary)]">
                  {ACTION_LABEL[a.action] || a.action}
                </div>
                <div className="text-xs text-[color:var(--text-muted)] mt-0.5">
                  {renderDetails(a.action, a.details)}
                </div>
              </div>
              <div className="text-xs text-[color:var(--text-muted)] whitespace-nowrap">
                {timeAgo(a.at)}
              </div>
            </li>
          ))}
        </ol>
      )}

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmOpen(false)}
          data-testid="guardian-revoke-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-2xl border border-[color:var(--line)] p-6 shadow-xl"
          >
            <h3 className="font-serif text-2xl mb-1">Revoke consent?</h3>
            <p className="text-sm text-[color:var(--text-muted)]">
              {data.minor.name} will no longer be able to upgrade to a paid
              tier or involve a professional mediator. They can continue using
              the Free tier for their existing case.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 rounded-full border border-[color:var(--line)] bg-white text-sm font-medium"
                data-testid="guardian-revoke-cancel"
              >
                Cancel
              </button>
              <button
                onClick={revoke}
                disabled={revoking}
                className="px-5 py-2 rounded-full bg-[color:var(--counter-error)] text-white text-sm font-semibold disabled:opacity-50"
                data-testid="guardian-revoke-confirm"
              >
                {revoking ? "Revoking…" : "Yes, revoke"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div
      className={`rounded-2xl border bg-white p-5 ${
        accent === "warn"
          ? "border-yellow-200"
          : "border-[color:var(--line)]"
      }`}
    >
      <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">
        {label}
      </div>
      <div className="font-serif text-3xl mt-1">{value}</div>
    </div>
  );
}
